
import { Component, Watch } from 'vue-property-decorator'
import { Debounce } from 'vue-debounce-decorator'

import { Mixins } from 'vue-mixin-decorator'
import MixinInterface from '@/_mixins/index'
import ProjectMixin from '@/_mixins/project'
import UserMixin from '@/_mixins/user'

import { searchProjectGroups, searchProjects, initiatePostProjectGroup } from '@/utils/project'
import { minSearchTermLength } from '@/_type/const'

import ToolbarComponent from '@/_components/bar/appToolbar/appToolbar.vue'
import ProjectListItem from '@/views/User/views/MyProjects/components/projectListItem.vue'
import ProjectGroupItem from '@/views/User/views/MyProjects/components/projectGroupListItem.vue'

@Component({
    components: { ToolbarComponent, ProjectListItem, ProjectGroupItem },
    mixins: [ProjectMixin, UserMixin],
})
export default class MyProjects extends Mixins<MixinInterface>(ProjectMixin, UserMixin) {
    minSearchTermLength = minSearchTermLength

    mounted() {
        if (this.isAuthenticated) this.loadGroups()
    }

    get groupListOpen() {
        return typeof this.$store.getters?.frontEndSettings?.groupListOpen == 'boolean'
            ? this.$store.getters.frontEndSettings.groupListOpen
            : true
    }

    set groupListOpen(data: any) {
        let frontEndSettings = {
            ...(this.frontEndSettings && { ...this.frontEndSettings }),
            groupListOpen: data,
        }
        this.$store.commit('frontEndSettings', frontEndSettings)
    }

    get includeArchived() {
        return typeof this.$store.getters?.frontEndSettings?.includeArchived == 'boolean'
            ? this.$store.getters.frontEndSettings.includeArchived
            : true
    }

    set includeArchived(data: any) {
        let frontEndSettings = {
            ...(this.frontEndSettings && { ...this.frontEndSettings }),
            includeArchived: data,
        }
        this.$store.commit('frontEndSettings', frontEndSettings)
    }

    get frontEndSettingsBase() {
        return this.$store.getters?.user?.attributes.frontEndSettings
            ? JSON.parse(this.$store.getters.user.attributes.frontEndSettings)
            : {}
    }

    get frontEndSettings() {
        return this.$store?.getters?.frontEndSettings
    }

    get frontEndSettingsUpdated() {
        return this.difference(this.frontEndSettings, this.frontEndSettingsBase)
    }

    @Watch('frontEndSettingsUpdated')
    @Debounce(2000)
    async updateUserSettings() {
        if (!this.isObjectEmpty(this.frontEndSettingsUpdated)) {
            let params = {
                updatedAt: this.$store.getters.user?.attributes.updatedAt,
                data: {
                    id: this.$store.getters.user.id,
                    type: this.$store.getters.user.type,
                    attributes: {
                        frontEndSettings: JSON.stringify(this.$store.getters?.frontEndSettings),
                    },
                },
            }

            await this.handlePatchUser(params)
        }
    }

    async handlePatchUser(params: any) {
        await this.patchUser(params).catch(async (error: any) => {
            if (error.data.response.status === 412) {
                await this.getUser(this.$store.getters.user.id).catch(() => {
                    //
                })

                params.updatedAt = this.$store.getters.user?.attributes.updatedAt

                await this.handlePatchUser(params)
            }
        })
    }

    // GET PROJECT
    debounceisOn = true
    searchTerm = ''
    offset = 0
    limit = 20
    identifier = +new Date()
    projects: Array<Record<string, any>> = []

    async loadProjects($state: any) {
        const filters = {
            ...(this.searchTerm?.trim().length >= this.minSearchTermLength && {
                'filter[searchTerm]': this.searchTerm,
            }),
            'page[offset]': this.offset,
            'page[limit]': this.limit,
        }

        const newProjects = await searchProjects(filters).catch(() => {
            $state.complete()
        })

        this.projects.push(...newProjects)

        if (newProjects.length == 20) {
            this.offset += 20
            $state.loaded()
        } else {
            if (this.projects.length > 0) {
                $state.loaded()
            }
            $state.complete()
        }

        this.debounceisOn = false
    }

    @Watch('searchTerm')
    handleDebounce() {
        this.debounceisOn = true
        this.projects = []
    }

    @Watch('searchTerm')
    @Debounce(500)
    updateSearch() {
        if (!this.searchTerm || this.searchTerm.length >= this.minSearchTermLength) {
            this.offset = 0
            this.identifier += 1
        } else {
            this.debounceisOn = false
        }
    }

    // PROJECT GROUP
    projectGroups: Array<Record<string, any>> = []
    loadingGroups = false

    async loadGroups() {
        try {
            this.loadingGroups = true
            this.projectGroups = await searchProjectGroups()
        } finally {
            this.loadingGroups = false
        }
    }

    get archivedProjects() {
        return this.$store.getters.archivedProjects
    }

    set archivedProjects(data: boolean) {
        this.$store.commit('archivedProjects', data)
    }

    // NEW PROJECT GROUP
    async createGroup() {
        const newProjectGroup = await initiatePostProjectGroup()

        if (newProjectGroup) {
            this.projectGroups.push(newProjectGroup)
        }
    }

    destroyed() {
        this.$store.commit('resetProject')
    }

    async beforeRouteLeave(to: any, from: any, next: any) {
        if (this.frontEndSettingsUpdated) {
            await this.updateUserSettings()
            next()
        } else {
            next()
        }
    }
}
